/**
 * @file
 * Real Estate CTA investor mode toggle behavior.
 *
 * @description
 * Syncs the investor mode switch with a session cookie so the preference
 * persists across page navigations and new tabs within the same browser session.
 * When active, reveals the rental yield calculator and swaps the primary
 * CTA button label/href to point to that section.
 */

import { getInvestorMode, setInvestorMode, onInvestorModeChange } from '../../src/investor-mode.ts';

((Drupal, once) => {
  'use strict';

  // Tracks ResizeObservers per CTA element so they can be disconnected on detach.
  const ctaHeightObservers = new WeakMap<HTMLElement, ResizeObserver>();

  /**
   * Publishes the rendered CTA height as a CSS custom property.
   *
   * @description
   * The sticky table-of-contents sidebar reads `--real-estate-cta-height` so it
   * can sit directly below the sticky CTA bar on both mobile and desktop. The
   * value is kept in sync with the CTA's rendered height via a ResizeObserver.
   *
   * @param cta - The CTA bar element to measure.
   */
  function trackCtaHeight(cta: HTMLElement): void {
    const updateHeight = (): void => {
      document.documentElement.style.setProperty('--real-estate-cta-height', `${cta.offsetHeight}px`);
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(cta);
    ctaHeightObservers.set(cta, observer);
  }

  /**
   * Applies investor mode state to the CTA button and calculator section.
   *
   * @param active - Whether investor mode is currently enabled.
   */
  function applyInvestorMode(active: boolean): void {
    // Toggle calculator visibility.
    const section = document.querySelector<HTMLElement>('[data-investor-section]');
    if (section) {
      if (active) {
        section.removeAttribute('hidden');
      } else {
        section.setAttribute('hidden', '');
      }
    }

    // Swap CTA button label and href across every CTA wrapper (top bar and the
    // mobile bottom bar both carry `data-investor-cta`).
    const wrappers = document.querySelectorAll<HTMLElement>('[data-investor-cta]');
    wrappers.forEach((wrapper) => {
      const link = wrapper.querySelector<HTMLAnchorElement>('a.button');
      const label = wrapper.querySelector<HTMLElement>('.button__label');
      if (!link || !label) return;

      if (active) {
        label.textContent = wrapper.dataset.investorLabel || '';
        link.setAttribute('href', wrapper.dataset.investorHref || '#');
      } else {
        label.textContent = wrapper.dataset.defaultLabel || '';
        link.setAttribute('href', wrapper.dataset.defaultHref || '#');
      }
    });
  }

  /**
   * Initializes the mobile overflow menu.
   *
   * @param mobileFooter - The mobile CTA footer wrapper.
   */
  function setupMobileMenu(mobileFooter: HTMLElement): void {
    const toggle = mobileFooter.querySelector<HTMLButtonElement>('.real-estate-cta-mobile__toggle');
    const menu = mobileFooter.querySelector<HTMLElement>('[data-mobile-menu]');

    if (!toggle || !menu) return;

    const closeMenu = (): void => {
      if (toggle.getAttribute('aria-expanded') !== 'true') return;
      toggle.setAttribute('aria-expanded', 'false');
      menu.hidden = true;
    };

    const openMenu = (): void => {
      toggle.setAttribute('aria-expanded', 'true');
      menu.hidden = false;
      menu.querySelector<HTMLElement>('input, button, a')?.focus();
    };

    toggle.setAttribute('aria-expanded', 'false');
    menu.hidden = true;

    toggle.addEventListener('click', (event) => {
      event.preventDefault();
      if (toggle.getAttribute('aria-expanded') === 'true') {
        closeMenu();
      } else {
        openMenu();
      }
    });

    const handleDocumentClick = (event: MouseEvent) => {
      if (toggle.getAttribute('aria-expanded') !== 'true') return;
      const target = event.target as Node | null;
      if (!target || mobileFooter.contains(target)) return;
      closeMenu();
    };

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
        toggle.focus();
      }
    };

    const handleFocusOut = (event: FocusEvent) => {
      const relatedTarget = event.relatedTarget as Node | null;
      if (relatedTarget && mobileFooter.contains(relatedTarget)) return;
      window.setTimeout(() => {
        if (toggle.getAttribute('aria-expanded') === 'true' && !mobileFooter.contains(document.activeElement)) {
          closeMenu();
        }
      }, 0);
    };

    document.addEventListener('click', handleDocumentClick);
    mobileFooter.addEventListener('keydown', handleKeydown);
    mobileFooter.addEventListener('focusout', handleFocusOut);
  }

  /**
   * Initializes the investor mode toggle switch.
   *
   * @description
   * Sets the switch checked state from the session cookie on attach,
   * keeps the stored value in sync when the user toggles the switch,
   * and controls the visibility of investor-specific page sections.
   * Also reacts to changes broadcast from other open tabs.
   */
  // React to investor mode changes broadcast from other tabs.
  onInvestorModeChange((active) => {
    applyInvestorMode(active);
    document.querySelectorAll<HTMLInputElement>('[data-investor-mode-switch]').forEach((toggle) => {
      toggle.checked = active;
    });
  });

  Drupal.behaviors.tpRealEstateCta = {
    attach(context: Document | HTMLElement) {
      // Expose the sticky CTA height so the ToC can offset itself below it.
      const ctas = once('tp-real-estate-cta-height', '.real-estate-cta', context);
      ctas.forEach((element) => {
        trackCtaHeight(element as HTMLElement);
      });

      const mobileFooters = once('tp-real-estate-cta-mobile-menu', '.real-estate-cta-mobile', context);
      mobileFooters.forEach((element) => {
        setupMobileMenu(element as HTMLElement);
      });

      const switches = once('tp-investor-mode-switch', '[data-investor-mode-switch]', context);

      if (switches.length > 0) {
        switches.forEach((element) => {
          const toggle = element as HTMLInputElement;
          const active = getInvestorMode() ?? false;

          // Set initial state from session cookie (default: false).
          toggle.checked = active;
          applyInvestorMode(active);

          toggle.addEventListener('change', () => {
            setInvestorMode(toggle.checked);
            applyInvestorMode(toggle.checked);
          });
        });
      }

      // Smooth-scroll when the investor CTA link is clicked.
      const ctaWrappers = once('tp-investor-cta-scroll', '[data-investor-cta]', context);
      if (ctaWrappers.length > 0) {
        ctaWrappers.forEach((wrapper) => {
          const link = wrapper.querySelector<HTMLAnchorElement>('a.button');
          if (!link) return;

          link.addEventListener('click', (event) => {
            const href = link.getAttribute('href') || '';
            if (!href.startsWith('#')) return;

            const target = document.querySelector<HTMLElement>(href);
            if (!target) return;

            event.preventDefault();
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        });
      }
    },

    detach(context: Document | HTMLElement, _settings: unknown, trigger?: string) {
      if (trigger !== 'unload') return;

      const root = context instanceof HTMLElement ? context : document;
      root.querySelectorAll<HTMLElement>('.real-estate-cta').forEach((cta) => {
        const observer = ctaHeightObservers.get(cta);
        if (observer) {
          observer.disconnect();
          ctaHeightObservers.delete(cta);
        }

        // Only clear the CSS property when the CTA element itself is being
        // unloaded. When AJAX replaces a narrower context (e.g. the views
        // results wrapper) that does NOT contain the CTA, we must not remove
        // the property — the CTA and its ResizeObserver are still active.
        document.documentElement.style.removeProperty('--real-estate-cta-height');
      });
    },
  };
})(Drupal, once);
